import {
  ReportLineItemNotation,
  ReportLineItemNotations,
  ReportGeneralInfo,
  ReportLineItem,
  ReportLineItemTypes,
  CA_REPORT,
  ReportStatusEnum,
} from './report.type';
import { CompInfoDTO } from '../comp/comp.type';
import { ProjectDraw, ProjectInfoDTO } from '../project/project.type';
import { REPORT_COLORS, DIVISION_COLORS, TOTAL_ID, ALLOWANCES_TOTAL_ID } from './constants';
import { getProjectAddressString } from '../project/project.util';
import { Division, LineItemUnitEnum } from '../glossary/glossary.type';

export const tableRowTypes = [ReportLineItemTypes.NORMAL, ReportLineItemTypes.CompPricing, ReportLineItemTypes.Adjustment];

export const tableRowsComparator = (a: ReportLineItem, b: ReportLineItem) => {
  // First priority: Normal rows (type is null or Normal)
  if ((a.type == null || a.type === ReportLineItemTypes.NORMAL) && b.type !== ReportLineItemTypes.NORMAL && b.type != null) return -1;
  if ((b.type == null || b.type === ReportLineItemTypes.NORMAL) && a.type !== ReportLineItemTypes.NORMAL && a.type != null) return 1;

  // Second priority: Adjustment rows
  if (a.type === ReportLineItemTypes.Adjustment && b.type === ReportLineItemTypes.CompPricing) return -1;
  if (b.type === ReportLineItemTypes.Adjustment && a.type === ReportLineItemTypes.CompPricing) return 1;

  return 0;
};

export const isTotalRow = (row: any) => {
  return (
    [
      ReportLineItemTypes.DivisionTotal,
      ReportLineItemTypes.HardCostSubTotal,
      ReportLineItemTypes.TotalProjectedCosts,
      ReportLineItemTypes.TotalProjectedHardCosts,
      ReportLineItemTypes.TotalSoftCosts,
    ].includes(row.type) ||
    row.id === TOTAL_ID ||
    row.id === ALLOWANCES_TOTAL_ID
  );
};

export const calculateCdccCompTotal = (row: any) => {
  if (isTotalRow(row)) {
    return row.cdccCompTotal; //return the already calculated total
  } else {
    return row.cdccCompPerUnit * row.quantity;
  }
};
export const calculateDeviationPercentage = (row: any) => {
  const compTotal = calculateCdccCompTotal(row);
  const budget = row.originalBudget ?? row.totalBudget;

  return compTotal > 0 && budget ? budget / compTotal - 1 : 0;
};

export const calculateUnitCost = (row: any) => {
  if (isTotalRow(row)) {
    return 0;
  }
  const budget = row.originalBudget ?? row.totalBudget;
  return row.quantity > 0 ? budget / row.quantity : 0;
};

export const calculateDeviation = (row: any) => {
  const budget = row.originalBudget ?? row.totalBudget;
  return budget - calculateCdccCompTotal(row);
};

export const getNotations: (row: any) => ReportLineItemNotation = (row: any) => {
  const deviationPercentage = calculateDeviationPercentage(row);
  if (deviationPercentage == 0) return null;
  if (deviationPercentage < -0.1) return ReportLineItemNotations.LOWER_THAN_NORMAL;
  if (deviationPercentage >= -0.1 && deviationPercentage <= 0.1) return ReportLineItemNotations.ACCEPTABLE;
  if (deviationPercentage > 0.1) return ReportLineItemNotations.HIGHER_THAN_NORMAL;
  return null;
};

export const getNotationColor = (notation: ReportLineItemNotation | string) => {
  switch (notation) {
    case ReportLineItemNotations.LOWER_THAN_NORMAL:
      return 'red';
    case ReportLineItemNotations.HIGHER_THAN_NORMAL:
      return 'red';
    case ReportLineItemNotations.ACCEPTABLE:
      return 'green';
    default:
      return 'black';
  }
};

export const getDivisionColor = (divisionCode: string) => {
  const DEFAULT_DIVISION_COLOR = REPORT_COLORS.YELLOW;
  return DIVISION_COLORS[divisionCode] || DEFAULT_DIVISION_COLOR;
};

export const getTotalsRow = (
  description: string,
  gridData: any[],
  grossBuildingSF: number,
  divisionCode: string,
  hardCostSubTotal?: number,
  totalProjectCost?: number,
  totalLineItem?: ReportLineItem,
  showRevisedBudget = false,
) => {
  const isPercentageDivision = ['1.00', '17.00', '18.00'].includes(divisionCode);
  const isAllowances = divisionCode === 'allowances' || description === 'ALLOWANCES TOTAL';
  let totalBudget;
  if (showRevisedBudget) {
    // coming from Project Analysis
    totalBudget = gridData.reduce((acc, row) => acc + row.originalBudget, 0);
  } else {
    totalBudget = gridData.reduce((acc, row) => acc + row.totalBudget, 0);
  }

  let revisedBudget;
  if (showRevisedBudget) {
    revisedBudget = gridData.reduce((acc, row) => {
      let revisedRowBudget;
      if (row.totalBudget) revisedRowBudget = row.totalBudget;
      else revisedRowBudget = (row?.originalBudget || 0) + (row?.reallocation || 0) + (row?.changeOrder || 0);
      return acc + revisedRowBudget;
    }, 0);
  }
  let isProjectAnalysisTotals = false;
  if (
    (description === 'HARD COST SUBTOTAL' ||
      description === 'TOTAL PROJECTED HARD COSTS' ||
      description === 'TOTAL SOFT COSTS' ||
      description === 'TOTAL PROJECTED COSTS') &&
    showRevisedBudget
  ) {
    totalBudget = revisedBudget;
    isProjectAnalysisTotals = true;
  }
  const totalCdccCompPerUnit = gridData.reduce((acc, row) => acc + row.cdccCompPerUnit, 0);
  const totalCdccCompTotal = gridData.reduce((acc, row) => acc + calculateCdccCompTotal(row), 0);
  let quantity = 0,
    cdccCompPerUnit = 0;
  const totalBudgetForQuantity = showRevisedBudget ? revisedBudget : totalBudget;
  if (hardCostSubTotal && isPercentageDivision) {
    quantity = (totalBudgetForQuantity / hardCostSubTotal) * 100;
    cdccCompPerUnit = Math.round((totalCdccCompPerUnit / hardCostSubTotal) * 10000) / 100;
  } else if (totalProjectCost && isAllowances) {
    quantity = (totalBudgetForQuantity / totalProjectCost) * 100;
    cdccCompPerUnit = (totalCdccCompPerUnit / totalProjectCost) * 100;
  } else {
    quantity = grossBuildingSF > 0 ? totalBudgetForQuantity / grossBuildingSF : 0;
    cdccCompPerUnit = totalCdccCompPerUnit;
  }
  const assignedId = isAllowances ? ALLOWANCES_TOTAL_ID : totalLineItem?.id || TOTAL_ID;

  const unit = isPercentageDivision || isAllowances ? '%' : '/SF';
  return {
    ...totalLineItem,
    id: assignedId,
    ...(isProjectAnalysisTotals
      ? { totalBudget: totalBudget }
      : showRevisedBudget
        ? { originalBudget: totalBudget }
        : { totalBudget: totalBudget }),
    ...(showRevisedBudget && { revisedBudget: revisedBudget }),
    lineItem: {
      id: totalLineItem?.lineItem?.id || TOTAL_ID,
      description: totalLineItem?.lineItem?.description || description,
      unit: '',
    },
    quantity: quantity,
    unit: unit,
    cdccCompPerUnit: cdccCompPerUnit,
    cdccCompTotal: totalCdccCompTotal,
  };
};

export const extractProjectsFromReportDraws = (draws: ProjectDraw[]) => {
  const projects: ProjectInfoDTO[] = [];
  draws.forEach(draw => {
    const currentProject = draw.project;
    if (!currentProject) return;
    if (projects.findIndex(project => project.projectId === currentProject.projectId) === -1) projects.push(currentProject);
  });
  return projects;
};

export const isValidGeneralInfo = (generalInfo: ReportGeneralInfo) => {
  return generalInfo?.reportName !== '';
};

export const getCompInfoString = (compInfo: CompInfoDTO) => {
  let result = `${compInfo.projectNumber}`;
  const address = getProjectAddressString(compInfo.projectAddress);
  if (address) result += `\n ${address}`;
  if (compInfo.projectTypeDescription) result += `\n ${compInfo.projectTypeDescription}`;
  if (compInfo.buildingCount) result += `\n ${compInfo.buildingCount} bldgs`;
  if (compInfo.unitCount) result += ` \n ${compInfo.unitCount} units`;
  if (compInfo.grossBuildingArea) result += `\n ${compInfo.grossBuildingArea} gsf`;
  if (compInfo.netArea) result += `\n ${compInfo.netArea} nsf`;
  if (compInfo.siteArea) result += `\n ${compInfo.siteArea} acres`;
  if (compInfo.storyCount) result += `\n ${compInfo.storyCount} stories`;

  return result;
};
export const compareArrays = (arr1: any, arr2: any) => {
  const json1 = JSON.stringify(arr1);
  const json2 = JSON.stringify(arr2);
  return json1 === json2;
};

export const getQuantity = (unit: any, generalInfo: ReportGeneralInfo) => {
  switch (unit) {
    case LineItemUnitEnum.SF:
      return generalInfo.grossBuildingSF || 0;
    case LineItemUnitEnum.LS:
      return 1;
    case LineItemUnitEnum.Units:
      return generalInfo.unitCount || 0;
    case LineItemUnitEnum.Stories:
      return generalInfo.storyCount || 0;
    case LineItemUnitEnum.Buildings:
      return generalInfo.buildingCount || 0;
    case LineItemUnitEnum.Stops:
      return generalInfo?.elevatorCount || 0;
    default:
      return 0;
  }
};
export const TOOLTIPS = {
  unlockToEdit: 'Unlock report to edit',
  statusCompleted: 'Report is marked as completed and cannot be edited',
};
export interface ConditionalTooltipProps {
  children: React.ReactNode;
  isLocked: boolean;
  reportStatus: string;
}

export const isReportCompleted = (report: CA_REPORT) => {
  return report?.status === ReportStatusEnum.Completed;
};

interface GenerateNotesParams {
  report: CA_REPORT;
  sortedDivisions: Division[];
  divisionTables: { [key: string]: any[] };
  projectedHardCostSubTotalRow: any[];
  totalProjectCostsRow: any[];
  overheadAndProfitPercentage: number | null;
}

export const generateHigherDivisionsNote = ({ report, sortedDivisions, divisionTables, totalProjectCostsRow }: GenerateNotesParams) => {
  // Get divisions with "Higher than Normal" notation
  const highDeviationDivisions = sortedDivisions
    .map(division => {
      const totalsRow = divisionTables[division.id]?.find(row => row.type === ReportLineItemTypes.DivisionTotal);
      if (!totalsRow) return null;

      const notation = totalsRow.notation || getNotations(totalsRow);
      if (notation === ReportLineItemNotations.HIGHER_THAN_NORMAL) {
        return {
          name: division.name,
          deviation: calculateDeviationPercentage(totalsRow) * 100,
        };
      }
      return null;
    })
    .filter(Boolean);

  if (highDeviationDivisions.length === 0) return null;

  // Check Total Projected Cost notation
  const totalProjectedCostRow = totalProjectCostsRow[0];
  const isTotalAcceptable =
    totalProjectedCostRow &&
    (totalProjectedCostRow.notation === ReportLineItemNotations.ACCEPTABLE ||
      getNotations(totalProjectedCostRow) === ReportLineItemNotations.ACCEPTABLE);

  // Generate the note
  let note = `The Contractor's costs for ${
    highDeviationDivisions.length > 1 ? 'a few divisions are' : 'division is'
  } higher than our estimation for this work, and outside our normally acceptable level of deviation. `;

  // Add division details
  note += highDeviationDivisions
    .map((div, index) => {
      if (!div) return '';
      const isLast = index === highDeviationDivisions.length - 1;
      const isSecondToLast = index === highDeviationDivisions.length - 2;

      let text = `The ${div.name} division is ${div.deviation.toFixed(1)}% higher`;
      if (isLast && highDeviationDivisions.length > 1) {
        text = `and the ${div.name} division is ${div.deviation.toFixed(1)}% higher`;
      } else if (!isLast && !isSecondToLast) {
        text += ',';
      }
      return text;
    })
    .join(' ');

  note += `. Although our projections are based on similar ${
    report.project?.projectTypeDescription || 'construction'
  } projects, the Contractor's costs are still more than expected.`;

  // Add the optional last line if Total Projected Cost is acceptable
  if (isTotalAcceptable) {
    note += ' Since the Total Projected Cost is within our acceptable level of deviation, we are not concerned with this difference.';
  }

  return note;
};

export const generateLowerDivisionsNote = ({ report, sortedDivisions, divisionTables, totalProjectCostsRow }: GenerateNotesParams) => {
  // Get divisions with "Lower than Normal" notation
  const lowDeviationDivisions = sortedDivisions
    .map(division => {
      const totalsRow = divisionTables[division.id]?.find(row => row.type === ReportLineItemTypes.DivisionTotal);
      if (!totalsRow) return null;

      const notation = totalsRow.notation || getNotations(totalsRow);
      if (notation === ReportLineItemNotations.LOWER_THAN_NORMAL) {
        return {
          name: division.name,
          deviation: Math.abs(calculateDeviationPercentage(totalsRow) * 100),
        };
      }
      return null;
    })
    .filter(Boolean);

  if (lowDeviationDivisions.length === 0) return null;

  // Check Total Projected Cost notation
  const totalProjectedCostRow = totalProjectCostsRow[0];
  const isTotalAcceptable =
    totalProjectedCostRow &&
    (totalProjectedCostRow.notation === ReportLineItemNotations.ACCEPTABLE ||
      getNotations(totalProjectedCostRow) === ReportLineItemNotations.ACCEPTABLE);

  // Generate the note
  let note = `The Contractor's costs for ${
    lowDeviationDivisions.length > 1 ? 'a few divisions are' : 'division is'
  } lower than our estimation for this work, and outside our normally acceptable level of deviation. `;

  // Add division details
  note += lowDeviationDivisions
    .map((div, index) => {
      if (!div) return '';
      const isLast = index === lowDeviationDivisions.length - 1;
      const isSecondToLast = index === lowDeviationDivisions.length - 2;

      let text = `The ${div.name} division is ${div.deviation.toFixed(1)}% lower`;
      if (isLast && lowDeviationDivisions.length > 1) {
        text = `and the ${div.name} division is ${div.deviation.toFixed(1)}% lower`;
      } else if (!isLast && !isSecondToLast) {
        text += ',';
      }
      return text;
    })
    .join(' ');

  note += `. Although our projections are based on similar ${
    report.project?.projectTypeDescription || 'construction'
  } projects, the Contractor's costs are still less than expected.`;

  // Add the optional last line if Total Projected Cost is acceptable
  if (isTotalAcceptable) {
    note += ' Since the Total Projected Cost is within our acceptable level of deviation, we are not concerned with this difference.';
  }

  return note;
};

export const generateOHPNote = ({
  report,
  sortedDivisions,
  divisionTables,
  totalProjectCostsRow,
  overheadAndProfitPercentage,
}: GenerateNotesParams) => {
  // Get division 17.00 (OH&P)
  const ohpDivision = sortedDivisions.find(d => d.code === '17.00');
  if (!ohpDivision) return null;

  const ohpTotalsRow = divisionTables[ohpDivision.id]?.find(row => row.type === ReportLineItemTypes.DivisionTotal);
  if (!ohpTotalsRow) return null;

  const ohpNotation = ohpTotalsRow.notation || getNotations(ohpTotalsRow);

  // Check if we should generate the note and determine if it's higher or lower
  const isHigher =
    (overheadAndProfitPercentage !== null &&
      report.generalInfo?.costThreshold != null &&
      overheadAndProfitPercentage > (report.generalInfo.costThreshold || 0)) ||
    ohpNotation === ReportLineItemNotations.HIGHER_THAN_NORMAL;
  const isLower = ohpNotation === ReportLineItemNotations.LOWER_THAN_NORMAL;

  if (!isHigher && !isLower) return null;

  // Check Total Projected Cost notation
  const totalProjectedCostRow = totalProjectCostsRow[0];
  const isTotalAcceptable =
    totalProjectedCostRow &&
    (totalProjectedCostRow.notation === ReportLineItemNotations.ACCEPTABLE ||
      getNotations(totalProjectedCostRow) === ReportLineItemNotations.ACCEPTABLE);

  // Generate the note
  let note = `The Contractor's cost for the Overhead and Profit line item is ${
    isHigher ? 'higher' : 'lower'
  } than similar projects of this type, and outside our normally acceptable level of deviation.`;

  // Add the optional last line if Total Projected Cost is acceptable
  if (isTotalAcceptable) {
    note +=
      ' Since the Overhead and Profit are often negotiated item, and since the Total Projected Cost is within our acceptable level of deviation, we are not concerned with this difference.';
  }

  return note;
};

export const generateContingencyNote = ({ sortedDivisions, divisionTables }: GenerateNotesParams) => {
  // Find division 18 (General Contractor Contingency)
  const contingencyDivision = sortedDivisions.find(d => d.code === '18.00');
  if (!contingencyDivision) return null;

  // Check if division has any line items
  const contingencyLineItems = divisionTables[contingencyDivision.id]?.filter(
    row => row.type === null || row.type === ReportLineItemTypes.NORMAL,
  );

  // Only generate note if there are no line items
  if (contingencyLineItems && contingencyLineItems.length > 0) return null;

  return (
    `Neither the Contractor's budget nor our projections include a separate budget line item for Contingency. ` +
    `However, we recommend a 3% contingency to help alleviate the risk potential to the project, and an additional ` +
    `material escalation contingency of 3% to 5% to help alleviate any potential future cost escalations including ` +
    `potential new tariffs (thus a total contingency of 6% to 8%). Another option would be to consider a price ` +
    `escalation clause in the Owner / Contractor Agreement. Due to the ongoing tariff concerns and potential ` +
    `instability with global shipping, we also recommend allowing for additional time in the schedule as well as ` +
    `early buyout of long lead and potentially impacted materials and equipment.`
  );
};

export const generateLowContingencyNote = ({ sortedDivisions, divisionTables, projectedHardCostSubTotalRow }: GenerateNotesParams) => {
  // Find division 18 (General Contractor Contingency)
  const contingencyDivision = sortedDivisions.find(d => d.code === '18.00');
  if (!contingencyDivision) return null;

  // Get total budget from contingency division
  const contingencyTotal =
    divisionTables[contingencyDivision.id]?.reduce((total, row) => {
      if (row.type === null || row.type === ReportLineItemTypes.NORMAL) {
        return total + (row.totalBudget || 0);
      }
      return total;
    }, 0) || 0;

  // Get Total Projected Hard Cost
  const totalProjectedHardCost = projectedHardCostSubTotalRow[0]?.totalBudget || 0;

  // Calculate contingency percentage
  const contingencyPercentage = totalProjectedHardCost > 0 ? (contingencyTotal / totalProjectedHardCost) * 100 : 0;

  // Only generate note if contingency is present but below 5%
  if (contingencyTotal === 0 || contingencyPercentage >= 5) return null;

  return (
    `The Contractor's budget includes a separate budget line item for Contingency of approximately ${contingencyPercentage.toFixed(1)}%. ` +
    `However, we recommend a 3% contingency to help alleviate the risk potential to the project, and an additional ` +
    `material escalation contingency of 3% to 5% to help alleviate any potential future cost escalations including ` +
    `potential new tariffs (thus a total contingency of 6% to 8%). Another option would be to consider a price ` +
    `escalation clause in the Owner / Contractor Agreement. Due to the ongoing tariff concerns and potential ` +
    `instability with global shipping, we also recommend allowing for additional time in the schedule as well as ` +
    `early buyout of long lead and potentially impacted materials and equipment.`
  );
};

export const generateBuyoutNote = () => {
  return 'We recommend that the buyout of all major subcontractors take place before loan closing or within 90 days of loan closing and before any loan funds are used.';
};

export const parseDynamicDataString = (dynamicDataString: string) => {
  try {
    return JSON.parse(dynamicDataString);
  } catch (e) {
    return {};
  }
};
